import { useState, useEffect } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import { getCurrentUser } from '../../services/auth';
import { getSystemModels, getMyModels, type VirtualModelItem } from '../../services/virtualModels';

export default function VirtualModelsPage() {
  const [activeMenu, setActiveMenu] = useState('虚拟模特库');
  const [user, setUser] = useState<Awaited<ReturnType<typeof getCurrentUser>>>(null);
  const [modelTab, setModelTab] = useState<'system' | 'my'>('system');
  const [myModels, setMyModels] = useState<VirtualModelItem[]>([]);
  const [myModelsLoading, setMyModelsLoading] = useState(false);
  // 预览弹窗：优先播放视频
  const [previewModel, setPreviewModel] = useState<VirtualModelItem | null>(null);

  const systemModels = getSystemModels();
  const displayModels = modelTab === 'system' ? systemModels : myModels;

  useEffect(() => {
    getCurrentUser().then(setUser);
  }, []);

  useEffect(() => {
    if (modelTab !== 'my' || !user?.id) {
      if (modelTab !== 'my') setMyModels([]);
      return;
    }
    setMyModelsLoading(true);
    getMyModels(user.id).then((list) => {
      setMyModels(list);
      setMyModelsLoading(false);
    }).catch(() => setMyModelsLoading(false));
  }, [modelTab, user?.id]);

  const handleCreateModel = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/create-virtual-model');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex min-w-0">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        
        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-7xl mx-auto w-full min-w-0">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold mb-2">虚拟模特库</h1>
                <p className="text-gray-400 text-sm">
                  内置海量AI虚拟人物素材，支持快速匹配不同风格的角色形象，解决模特聘用难、成本高的问题
                </p>
              </div>
              <button
                onClick={handleCreateModel}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-add-line text-xl"></i>
                <span>创建模特</span>
              </button>
            </div>

            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setModelTab('system')}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  modelTab === 'system' ? 'bg-blue-600 text-white' : 'bg-[#1a1a1a] text-gray-400 border border-gray-700 hover:border-blue-500'
                }`}
              >
                系统模特
              </button>
              <button
                type="button"
                onClick={() => setModelTab('my')}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  modelTab === 'my' ? 'bg-blue-600 text-white' : 'bg-[#1a1a1a] text-gray-400 border border-gray-700 hover:border-blue-500'
                }`}
              >
                我的模特
              </button>
            </div>

            {myModelsLoading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>
                加载中…
              </div>
            ) : modelTab === 'my' && !user ? (
              <div className="py-20 text-center text-gray-400">
                请先登录后查看我的模特
              </div>
            ) : modelTab === 'my' && displayModels.length === 0 ? (
              <div className="py-20 text-center text-gray-400">
                暂无我的模特，
                <button type="button" onClick={() => window.REACT_APP_NAVIGATE?.('/create-virtual-model')} className="text-blue-400 hover:underline ml-1 cursor-pointer">去创建</button>
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {displayModels.map((model) => (
                <div
                  key={model.id}
                  className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-gray-800 hover:border-blue-500/50 transition-all group cursor-pointer"
                >
                  <div className="relative w-full h-80 bg-black">
                    {model.video ? (
                      <video
                        src={model.video}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                        onMouseEnter={(e) => {
                          const v = e.currentTarget;
                          v.play().catch(() => {});
                        }}
                        onMouseLeave={(e) => {
                          const v = e.currentTarget;
                          v.pause();
                          v.currentTime = 0;
                        }}
                      />
                    ) : (
                      <img
                        src={model.image}
                        alt={model.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.REACT_APP_NAVIGATE?.('/sora2-video');
                            }}
                            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                          >
                            <i className="ri-play-line"></i>
                            <span>使用</span>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewModel(model);
                            }}
                            className="px-4 py-2 bg-[#0a0a0a]/80 hover:bg-[#0a0a0a] rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
                            title="预览"
                          >
                            <i className="ri-eye-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-semibold mb-2 truncate">{model.name}</h3>
                    <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-medium ${model.isMine ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {model.isMine ? '我的模特' : '系统模特'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </main>
      </div>

      {/* 模特预览：有视频则播放，否则展示封面图 */}
      {previewModel && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewModel(null)}
        >
          <div
            className="bg-[#1a1a1a] rounded-2xl border border-gray-800 max-w-3xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold truncate">{previewModel.name}</h3>
              <button
                type="button"
                onClick={() => setPreviewModel(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="bg-black aspect-video flex items-center justify-center">
              {previewModel.video ? (
                <video
                  src={previewModel.video}
                  controls
                  autoPlay
                  className="w-full h-full max-h-[70vh] object-contain"
                />
              ) : (
                <img
                  src={previewModel.image}
                  alt={previewModel.name}
                  className="w-full h-full max-h-[70vh] object-contain"
                />
              )}
            </div>
            {!previewModel.video && previewModel.isMine && (
              <p className="px-5 py-3 text-xs text-amber-400/90">
                该模特创建时未保存视频文件（旧数据），仅可查看封面。请重新「创建模特」并上传视频。
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
